import { useRef, useState, FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { DealerAuthProvider, useDealerAuth } from './context/DealerAuthContext';

function DealerLoginContent() {
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, sendResetPassword, configError } = useDealerAuth();
  const navigate = useNavigate();
  const loginFormRef = useRef<HTMLFormElement>(null);

  const handleLogin = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');
    setSuccessMessage('');

    const form = event.currentTarget;
    const email = (form.elements.namedItem('email') as HTMLInputElement).value;
    const password = (form.elements.namedItem('password') as HTMLInputElement).value;

    setLoading(true);
    const result = await login(email, password);
    setLoading(false);

    if (result.ok) {
      navigate('/dealer/dashboard');
    } else {
      setError(result.error || 'Invalid credentials. Please try again.');
    }
  };

  const handlePasswordReset = async (form: HTMLFormElement) => {
    const email = (form.elements.namedItem('email') as HTMLInputElement).value;
    if (!email) {
      setError('Enter your email first, then request a password reset.');
      return;
    }

    setError('');
    setSuccessMessage('');
    const result = await sendResetPassword(email);

    if (result.ok) {
      setSuccessMessage(result.message || 'Password reset email sent.');
    } else {
      setError(result.error || 'Unable to send password reset email.');
    }
  };

  return (
    <div className="min-h-screen bg-[#0D1B2A]">
      <Navbar />

      <section className="relative min-h-screen flex items-center justify-center py-24 px-6">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://readdy.ai/api/search-image?query=dark%20navy%20ocean%20water%20abstract%20texture%20deep%20sea%20background%20minimal%20dark%20blue%20waves%20pattern%20professional%20moody%20atmosphere%20high%20resolution%20seamless%20background&width=1440&height=900&seq=dealer-bg&orientation=landscape"
            alt=""
            className="w-full h-full object-cover object-top"
          />
        </div>

        <div className="relative z-10 w-full max-w-md">
          <div className="text-center mb-8">
            <img
              src="https://static.readdy.ai/image/3bab8804b943b0f9d2e99ce4e4e8123d/a7b2a29c0c21ce8564d3744200077816.png"
              alt="Stingray"
              className="h-10 w-auto object-contain brightness-0 invert mx-auto mb-4"
            />
            <div className="inline-flex items-center gap-2 bg-[#F97316]/20 border border-[#F97316]/30 text-[#F97316] text-xs font-bold tracking-widest uppercase px-4 py-1.5 rounded-full">
              <div className="w-3 h-3 flex items-center justify-center">
                <i className="ri-store-2-line"></i>
              </div>
              Dealer Portal
            </div>
          </div>

          <div className="bg-[#1C2B3A] rounded-lg p-8">
            <form ref={loginFormRef} onSubmit={handleLogin} className="space-y-5">
              <div>
                <p className="text-white text-2xl font-black mb-2">Sign in to your portal</p>
                <p className="text-white/50 text-sm">
                  Approved dealers can access pricing, inventory, and ordering from here.
                </p>
              </div>

              <div>
                <label className="block text-white/60 text-xs font-semibold uppercase tracking-wide mb-2">Email</label>
                <input
                  type="email"
                  name="email"
                  required
                  placeholder="dealer@company.com"
                  className="w-full bg-[#0D1B2A] border border-white/10 text-white text-sm px-4 py-3 rounded-md focus:outline-none focus:border-[#F97316] placeholder-white/30 transition-colors"
                />
              </div>

              <div>
                <label className="block text-white/60 text-xs font-semibold uppercase tracking-wide mb-2">Password</label>
                <div className="relative">
                  <input
                    type={showPass ? 'text' : 'password'}
                    name="password"
                    required
                    placeholder="••••••••"
                    className="w-full bg-[#0D1B2A] border border-white/10 text-white text-sm px-4 py-3 pr-10 rounded-md focus:outline-none focus:border-[#F97316] placeholder-white/30 transition-colors"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPass(!showPass)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center text-white/40 hover:text-white cursor-pointer"
                  >
                    <i className={showPass ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between text-xs">
                <label className="flex items-center gap-2 text-white/50 cursor-pointer">
                  <input type="checkbox" className="accent-[#F97316]" />
                  Remember me
                </label>
                <button
                  type="button"
                  onClick={() => {
                    if (loginFormRef.current) {
                      void handlePasswordReset(loginFormRef.current);
                    }
                  }}
                  className="text-[#F97316] hover:underline cursor-pointer whitespace-nowrap"
                >
                  Forgot password?
                </button>
              </div>

              {configError && (
                <p className="text-amber-300 text-xs bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2">
                  {configError}
                </p>
              )}
              {error && (
                <p className="text-red-400 text-xs bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">{error}</p>
              )}
              {successMessage && (
                <p className="text-green-400 text-xs bg-green-500/10 border border-green-500/20 rounded-lg px-3 py-2">{successMessage}</p>
              )}

              <button
                type="submit"
                disabled={loading || Boolean(configError)}
                className="w-full bg-[#F97316] text-white font-bold py-3.5 rounded-md hover:bg-[#ea6a0a] transition-colors cursor-pointer whitespace-nowrap text-sm uppercase tracking-wide disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? 'Signing In...' : 'Sign In to Portal'}
              </button>
            </form>
          </div>

          <p className="text-center text-white/30 text-xs mt-6">
            Need help?{' '}
            <Link to="/contact" className="text-[#F97316] hover:underline cursor-pointer">
              Contact our dealer support team
            </Link>
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default function DealerPage() {
  return (
    <DealerAuthProvider>
      <DealerLoginContent />
    </DealerAuthProvider>
  );
}
